import { useMutation, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";
import { AppStrings } from "../../utils/constants/AppStrings";
import { getApiErrorMessage } from "../../utils/ErrorUtils";
import { bonusAdminEnvelope } from "./bonusAdminBody";
import { getCampaignQueryKey } from "./useGetCampaign";
import { LIST_CAMPAIGNS_QUERY_KEY } from "./useListCampaigns";
import type { CampaignIn, CampaignOut } from "../../components/Promotions/DynamicBonuses/types";

type StoredToken = { AccessToken?: string };

type UpdateCampaignVars = { campaignId: string; payload: CampaignIn };

type UpdateCampaignResult = { body: CampaignOut; message?: string };

async function putCampaign({ campaignId, payload }: UpdateCampaignVars): Promise<UpdateCampaignResult> {
  const tokenString = localStorage.getItem("token");
  const token: StoredToken | null = tokenString ? JSON.parse(tokenString) : null;
  const instance = createPrivateAdminPanelInstance(token?.AccessToken);
  const url = API_ROUTES.UPDATE_DYNAMIC_BONUS_CAMPAIGN.replace("{campaign_id}", campaignId);
  const response = await instance.put(url, payload);
  const envelope = bonusAdminEnvelope<CampaignOut>(response);
  if (!envelope.body) {
    throw new Error("Campaign update returned no data");
  }
  return { body: envelope.body, message: envelope.message };
}

export const useUpdateCampaign = (campaignId: string) => {
  const queryClient = useQueryClient();

  return useMutation<UpdateCampaignResult, Error, CampaignIn>({
    mutationFn: (payload) => putCampaign({ campaignId, payload }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: LIST_CAMPAIGNS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: getCampaignQueryKey(campaignId) });
      message.success(result.message || AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED);
    },
    onError: (error) => {
      message.error(getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED));
    },
  });
};
